import ScrollToTop from "components/ScrollToTop";
import Forbidden from "pages/Forbidden";
import NotFound from "pages/NotFound";
import React, { lazy, Suspense } from "react";
import { BrowserRouter, Routes as RouterRoutes, Route, Navigate } from "react-router-dom";

import RBACTest from './components/RBACTest';
import RoleGuard from './components/RoleGuard';

// Lazy-loaded pages to reduce initial bundle size
const AdminDashboard = lazy(() => import('./pages/admin-dashboard'));
const AdminInvoiceManagement = lazy(() => import('./pages/admin-invoice-management'));
const AdminLeaseManagement = lazy(() => import('./pages/admin-lease-management'));
const AdminMaintenanceTickets = lazy(() => import('./pages/admin-maintenance-tickets'));
const AdminTenantCreate = lazy(() => import('./pages/admin-tenant-create'));
const TenantDashboard = lazy(() => import('./pages/tenant-dashboard'));
const AdminProfileSettings = lazy(() => import('./pages/admin-profile-settings'));
const AdminPropertyManagement = lazy(() => import('./pages/admin-property-management'));
const TenantProfileSettings = lazy(() => import('./pages/tenant-profile-settings'));
const AdminReports = lazy(() => import('./pages/admin-reports'));
const AdminSettingsPanel = lazy(() => import('./pages/admin-settings-panel'));
const PingLogin = lazy(() => import('./pages/dev/ping-login'));
const TenantBilling = lazy(() => import('./pages/tenant-billing'));
const TenantLogin = lazy(() => import('./pages/tenant-login'));
const TenantMaintenance = lazy(() => import('./pages/tenant-maintenance'));
const TenantRegister = lazy(() => import('./pages/tenant-register'));
const TenantDocuments = lazy(() => import('./pages/tenant/Documents'));
const SignLease = lazy(() => import('./pages/tenant/SignLease'));
const AdminTenantDocuments = lazy(() => import('./pages/admin/TenantDocuments'));
const AdminTenantLease = lazy(() => import('./pages/admin/TenantLease'));
const StatusPage = lazy(() => import('./pages/Status'));

const Routes = () => {
  return (
    <BrowserRouter>
      <ScrollToTop />
      <RouterRoutes>
                {/* Public Routes */}
                <Route path="/tenant-login" element={<Suspense fallback={null}><TenantLogin /></Suspense>} />
                <Route path="/admin-login" element={<Suspense fallback={null}><TenantLogin /></Suspense>} />
                <Route path="/tenant/register" element={<Suspense fallback={null}><TenantRegister /></Suspense>} />
                
                {/* Root redirect - redirect to appropriate dashboard based on user role */}
                <Route path="/" element={<Navigate to="/tenant-login" replace />} />
                
                {/* Tenant-only Routes */}
                <Route 
                  path="/tenant-dashboard" 
                  element={
                    <RoleGuard allowedRoles={['tenant']}>
                      <Suspense fallback={null}><TenantDashboard /></Suspense>
                    </RoleGuard>
                  }
                />
                <Route 
                  path="/tenant-maintenance" 
                  element={
                    <RoleGuard allowedRoles={['tenant']}>
                      <Suspense fallback={null}><TenantMaintenance /></Suspense>
                    </RoleGuard>
                  }
                />
                <Route 
                  path="/tenant-billing" 
                  element={
                    <RoleGuard allowedRoles={['tenant']}>
                      <Suspense fallback={null}><TenantBilling /></Suspense>
                    </RoleGuard>
                  }
                />
                <Route 
                  path="/tenant-profile-settings" 
                  element={
                    <RoleGuard allowedRoles={['tenant']}>
                      <Suspense fallback={null}><TenantProfileSettings /></Suspense>
                    </RoleGuard>
                  }
                />
                <Route 
                  path="/tenant/documents" 
                  element={
                    <RoleGuard allowedRoles={['tenant']}>
                      <Suspense fallback={null}><TenantDocuments /></Suspense>
                    </RoleGuard>
                  }
                />
                <Route 
                  path="/tenant/sign-lease" 
                  element={
                    <RoleGuard allowedRoles={['tenant']}>
                      <Suspense fallback={null}><SignLease /></Suspense>
                    </RoleGuard>
                  }
                />
                
                {/* Admin-only Routes */}
                <Route 
                  path="/admin-dashboard" 
                  element={
                    <RoleGuard allowedRoles={['admin', 'manager']}>
                      <Suspense fallback={null}><AdminDashboard /></Suspense>
                    </RoleGuard>
                  }
                />
                <Route 
                  path="/admin-maintenance-tickets" 
                  element={
                    <RoleGuard allowedRoles={['admin', 'manager']}>
                      <Suspense fallback={null}><AdminMaintenanceTickets /></Suspense>
                    </RoleGuard>
                  }
                />
                <Route 
                  path="/admin-property-management" 
                  element={
                    <RoleGuard allowedRoles={['admin', 'manager']}>
                      <Suspense fallback={null}><AdminPropertyManagement /></Suspense>
                    </RoleGuard>
                  }
                />
                <Route 
                  path="/admin-lease-management" 
                  element={
                    <RoleGuard allowedRoles={['admin', 'manager']}>
                      <Suspense fallback={null}><AdminLeaseManagement /></Suspense>
                    </RoleGuard>
                  }
                />
                <Route 
                  path="/admin-tenant-create" 
                  element={
                    <RoleGuard allowedRoles={['admin', 'owner']}>
                      <Suspense fallback={null}><AdminTenantCreate /></Suspense>
                    </RoleGuard>
                  }
                />
                <Route 
                  path="/admin-invoice-management" 
                  element={
                    <RoleGuard allowedRoles={['admin', 'manager']}>
                      <Suspense fallback={null}><AdminInvoiceManagement /></Suspense>
                    </RoleGuard>
                  }
                />
                <Route 
                  path="/admin-profile-settings" 
                  element={
                    <RoleGuard allowedRoles={['admin', 'manager']}>
                      <Suspense fallback={null}><AdminProfileSettings /></Suspense>
                    </RoleGuard>
                  }
                />
                <Route 
                  path="/admin-settings-panel" 
                  element={
                    <RoleGuard allowedRoles={['admin']}>
                      <Suspense fallback={null}><AdminSettingsPanel /></Suspense>
                    </RoleGuard>
                  }
                />
                
                {/* Redirect old routes to correct ones */}
                <Route path="/admin-profile" element={<Navigate to="/admin-profile-settings" replace />} />
                
                {/* Reports Route */}
                <Route 
                  path="/admin-reports" 
                  element={
                    <RoleGuard allowedRoles={['admin', 'manager']}>
                      <Suspense fallback={null}><AdminReports /></Suspense>
                    </RoleGuard>
                  }
                />
                <Route 
                  path="/admin/tenant-documents" 
                  element={
                    <RoleGuard allowedRoles={['admin', 'manager']}>
                      <Suspense fallback={null}><AdminTenantDocuments /></Suspense>
                    </RoleGuard>
                  }
                />
                <Route 
                  path="/admin/tenant-lease" 
                  element={
                    <RoleGuard allowedRoles={['admin', 'manager']}>
                      <Suspense fallback={null}><AdminTenantLease /></Suspense>
                    </RoleGuard>
                  }
                />
                
                {/* Test Routes */}
                <Route path="/rbac-test" element={<RBACTest />} />
                <Route path="/dev/ping-login" element={<Suspense fallback={null}><PingLogin /></Suspense>} />
                <Route path="/status" element={<Suspense fallback={null}><StatusPage /></Suspense>} />
                
                {/* Error Pages */}
                <Route path="/403" element={<Forbidden />} />
                <Route path="*" element={<NotFound />} />
      </RouterRoutes>
    </BrowserRouter>
  );
};

export default Routes;